import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DateTimePicker } from '@/components/sessions/DateTimePicker';
import { usePositionsStore } from '@/store/positionsStore';
import { useAuth } from '@/providers/AuthProvider';
import type { PageProps } from '@/types/page';
import type { UpdatePositionRequest, Position } from '@arrowise/shared';
import { ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function ManagePositionPage({ nav = true, footer = true }: PageProps) {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { getPositionById, updatePosition } = usePositionsStore();
    const { teams, getUserTeamRole } = useAuth();
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [position, setPosition] = useState<Position | null>(null);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        hiringEndDate: null as Date | null,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // Load position data
    useEffect(() => {
        const loadPosition = async () => {
            if (!id) return;

            try {
                const positionData = getPositionById(id);
                if (!positionData) {
                    toast.error('Position not found');
                    navigate('/app/positions');
                    return;
                }

                setPosition(positionData);
                setFormData({
                    name: positionData.name,
                    description: positionData.description || '',
                    hiringEndDate: positionData.hiringEndDate ? new Date(positionData.hiringEndDate) : null,
                });
            } catch (error) {
                toast.error('Failed to load position');
                navigate('/app/positions');
            } finally {
                setFetchLoading(false);
            }
        };

        loadPosition();
    }, [id, getPositionById, navigate]);

    // Check if user can manage this position
    const canManagePosition = () => {
        if (!position) return false;

        if (!teams || teams.length === 0) {
            // Individual position: only creator can manage
            return true; // Assume user is creator if they can see it
        }

        if (position.teamId) {
            // Team position: check if user is owner/admin
            const role = getUserTeamRole(position.teamId);
            return role === 'owner' || role === 'admin';
        }

        // Personal position in team context
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id || !position) return;

        // Basic validation
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Position name is required';
        }
        if (formData.name.length > 120) {
            newErrors.name = 'Position name must be 120 characters or less';
        }
        if (formData.description && formData.description.length > 2000) {
            newErrors.description = 'Description must be 2000 characters or less';
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            return;
        }

        setLoading(true);
        try {
            const payload: UpdatePositionRequest = {
                name: formData.name.trim(),
                description: formData.description.trim() || undefined,
                hiringEndDate: formData.hiringEndDate?.toISOString() || null,
            };

            await updatePosition(id, payload);
            navigate('/app/positions');
        } catch (error: any) {
            // Error handling is done in the store
        } finally {
            setLoading(false);
        }
    };

    const updateFormData = (updates: Partial<typeof formData>) => {
        setFormData(prev => ({ ...prev, ...updates }));
        // Clear errors for updated fields
        const updatedFields = Object.keys(updates);
        setErrors(prev => {
            const newErrors = { ...prev };
            updatedFields.forEach(field => delete newErrors[field]);
            return newErrors;
        });
    };

    if (fetchLoading) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-2xl w-full space-y-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
                        <div className="h-64 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (!position) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-2xl w-full space-y-8">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold">Position not found</h1>
                        <Link to="/app/positions">
                            <Button className="mt-4">Back</Button>
                        </Link>
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (!canManagePosition()) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-2xl w-full space-y-8">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold">Access Denied</h1>
                        <p className="text-muted-foreground mt-2">
                            You don't have permission to edit this position.
                        </p>
                        <Link to="/app/positions">
                            <Button className="mt-4">Back</Button>
                        </Link>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-10 mx-auto max-w-2xl w-full space-y-8">
                <div className="flex items-center gap-4">
                    <Link to="/app/positions">
                        <Button variant="outline" size="sm">
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                    </Link>
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Edit Position</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Update the details for "{position.name}".
                        </p>
                    </div>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Position Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="name">Position Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => updateFormData({ name: e.target.value })}
                                    placeholder="e.g. Senior Software Engineer"
                                    maxLength={120}
                                />
                                {errors.name && (
                                    <p className="text-sm text-destructive">{errors.name}</p>
                                )}
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => updateFormData({ description: e.target.value })}
                                    placeholder="Describe the role, requirements, and responsibilities..."
                                    rows={4}
                                    maxLength={2000}
                                />
                                {errors.description && (
                                    <p className="text-sm text-destructive">{errors.description}</p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                    {formData.description.length}/2000 characters
                                </p>
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label>Hiring End Date</Label>
                                <DateTimePicker
                                    value={formData.hiringEndDate}
                                    onChange={(hiringEndDate) => updateFormData({ hiringEndDate })}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Optional: Set a date when hiring for this position will end
                                </p>
                            </div>

                            <div className="flex items-center gap-2 pt-4">
                                <Button type="submit" disabled={loading}>
                                    {loading ? 'Updating...' : 'Update Position'}
                                </Button>
                                <Link to="/app/positions">
                                    <Button type="button" variant="outline">
                                        Cancel
                                    </Button>
                                </Link>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </PageLayout>
    );
}
